import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { APPOINTMENT_STATUS_GROUPS, type AppointmentView } from "@shared/modules/x-atelier";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Input } from "../../../core/components/ui/input";
import { PageHeader } from "../../../core/components/ui/page-header";
import { AppointmentModal } from "../components/appointment-modal";
import { JobRow } from "../components/job-row";
import { listAppointments } from "../lib/api";

const TABS = [...APPOINTMENT_STATUS_GROUPS, { key: "tous", label: "Tous", statuses: [] }];

/** Liste des travaux par onglets de statut, avec recherche ; rangées tactiles. */
export function TravauxPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("onglet");
  const tab = TABS.find((t) => t.key === rawTab) ?? TABS[1]!;
  const urlQuery = searchParams.get("q") ?? "";
  const [query, setQuery] = useState(urlQuery);
  const [debounced, setDebounced] = useState(urlQuery.trim());
  const [items, setItems] = useState<AppointmentView[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [creating, setCreating] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // La recherche de la barre du haut arrive par l'URL, même si la page est déjà ouverte.
  // Celle tapée ici y est écrite à son tour : « Retour » depuis une fiche
  // retrouve la liste telle qu'on l'avait laissée (onglet et recherche).
  const written = useRef(urlQuery);
  useEffect(() => {
    if (urlQuery === written.current) return;
    written.current = urlQuery;
    setQuery(urlQuery);
    setDebounced(urlQuery.trim());
  }, [urlQuery]);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (debounced === written.current.trim()) return;
    written.current = debounced;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debounced) next.set("q", debounced);
        else next.delete("q");
        return next;
      },
      { replace: true },
    );
  }, [debounced, setSearchParams]);

  const statuses = useMemo(() => tab.statuses, [tab]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listAppointments({
      status: statuses.length ? statuses : undefined,
      q: debounced || undefined,
      limit: 300,
    })
      .then((list) => {
        if (cancelled) return;
        // Les travaux récents d'abord : ce qu'on cherche est rarement vieux.
        setItems(tab.key === "planifies" || tab.key === "devis" ? list : [...list].reverse());
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [statuses, debounced, reloadKey, tab.key]);

  const selectTab = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("onglet", key);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="page travaux">
      <PageHeader
        title="Travaux"
        subtitle="Devis, rendez-vous planifiés, véhicules à l'atelier et travaux récupérés."
        actions={
          <Button
            variant="primary"
            icon={<Icon name="plus" size={18} />}
            onClick={() => setCreating(true)}
          >
            Nouveau rendez-vous
          </Button>
        }
      />

      <div className="chips chips--tabs" role="tablist" aria-label="Statut">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab.key === t.key}
            className={`chips__chip${tab.key === t.key ? " is-active" : ""}`}
            onClick={() => selectTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="atelier-toolbar">
        <div className="atelier-toolbar__search">
          <Input
            type="search"
            aria-label="Rechercher"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Client, véhicule, immatriculation…"
            enterKeyHint="search"
          />
        </div>
        <div className="atelier-toolbar__spacer" />
        <span className="atelier-toolbar__count">
          {loading ? "…" : `${items.length} travail${items.length > 1 ? "aux" : ""}`}
        </span>
      </div>

      {!loading && items.length === 0 ? (
        <p className="empty">
          {failed
            ? "Liste indisponible — réessayez dans un instant."
            : debounced
              ? "Aucun travail ne correspond à la recherche."
              : "Aucun travail dans cet onglet."}
        </p>
      ) : loading && items.length === 0 ? (
        <p className="empty">Chargement…</p>
      ) : (
        <div className="job-list" role="list">
          {items.map((a) => (
            <JobRow key={a.id} appointment={a} />
          ))}
        </div>
      )}

      <AppointmentModal
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={(a) => {
          setCreating(false);
          setReloadKey((k) => k + 1);
          navigate(`/travaux/${a.id}`);
        }}
      />
    </div>
  );
}
